import type { ReactNode } from 'react';
import {
  Controller,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';

import styles from './form-field.module.scss';

type RenderArgs<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = Parameters<ControllerProps<TFieldValues, TName>['render']>[0];

export type FormFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = {
  name: TName;
  control: ControllerProps<TFieldValues, TName>['control'];
  /** Plain string, or a node (e.g. a label with a trailing info-tooltip icon). */
  label?: ReactNode;
  help?: string;
  required?: boolean;
  /** Opt-in: keep the label in the accessible tree but out of the layout, for a field the
   *  design draws with no visible caption (the OTP cells, 110539). Unset everywhere else. */
  labelHidden?: boolean;
  /** Opt-in: suppress the field-level error message even when one is set. For a field whose own
   *  child already renders a more specific reason inline (e.g. `<Upload>`'s per-file rejection
   *  tile) — stacking the generic "required" message underneath it is redundant, not additive
   *  (ADO 119326). Every other consumer leaves this unset and keeps today's behaviour. */
  hideError?: boolean;
  children: (args: RenderArgs<TFieldValues, TName>) => ReactNode;
};

export function FormField<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  name,
  control,
  label,
  help,
  required,
  labelHidden,
  hideError,
  children,
}: FormFieldProps<TFieldValues, TName>) {
  return (
    <Controller
      name={name}
      control={control}
      render={(args) => (
        <div className={styles.field}>
          {label ? (
            <label
              className={[
                styles.label,
                required && !labelHidden ? styles.requiredLabel : null,
                labelHidden ? styles.labelHidden : null,
              ]
                .filter(Boolean)
                .join(' ')}
              htmlFor={name}
            >
              {label}
            </label>
          ) : null}
          {children(args)}
          {help ? <div className={styles.help}>{help}</div> : null}
          {args.fieldState.error?.message && !hideError ? (
            <div className={styles.error} role="alert">
              {args.fieldState.error.message}
            </div>
          ) : null}
        </div>
      )}
    />
  );
}
